import { useEffect, useState } from "react";

export type ThemeMode = "light" | "dark" | "system";
const KEY = "gb-theme";

function systemDark(): boolean {
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false;
}

function resolve(mode: ThemeMode): "light" | "dark" {
  return mode === "system" ? (systemDark() ? "dark" : "light") : mode;
}

function apply(mode: ThemeMode): void {
  document.documentElement.setAttribute("data-theme", resolve(mode));
}

function stored(): ThemeMode {
  const v = localStorage.getItem(KEY);
  return v === "light" || v === "dark" || v === "system" ? v : "system";
}

/** Kalles før React rendrer (i main.tsx) for å unngå lys-glimt. */
export function initTheme(): void {
  apply(stored());
}

export function useTheme() {
  const [mode, setMode] = useState<ThemeMode>(stored);

  useEffect(() => {
    apply(mode);
    localStorage.setItem(KEY, mode);
  }, [mode]);

  // Følg operativsystemets tema når vi står i "system"-modus.
  useEffect(() => {
    if (mode !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => apply("system");
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [mode]);

  return { mode, setMode };
}

// Stil: alltid ACLs glass-utseende (halvgjennomsiktige flater over fargeflekker), satt som
// data-style="glass" på <html>. Erik 02.11.2026: «Sett glass som standard og fjern DMF og Flat».
// Tidligere valg (gb-stil, gb-glass) i nettleseren overstyres.

/** Kalles før React rendrer (i main.tsx), som initTheme. */
export function initStil(): void {
  document.documentElement.dataset.style = "glass";
}

// Fargepalett, uavhengig av tema og stil. Settes som data-palett på <html>.
// «brun» (standard, Erik 31.10.2026): den opprinnelige GB-brunfargen og lys beige.
// «blaa» (ACL-blått og oransje) kan velges. «plomme» er fjernet (Erik 02.11.2026); den som
// hadde valgt den, får brun.
export type Palett = "brun" | "blaa";
// Ny nøkkel (før «gb-palett»): den gamle lagret «plomme» også for dem som aldri valgte
// selv, så alle starter på brun nå og velger på nytt om de vil.
const PALETT_KEY = "gb-farge";
const PALETTER: Palett[] = ["brun", "blaa"];

function readPalett(): Palett {
  try {
    const p = localStorage.getItem(PALETT_KEY) as Palett | null;
    return p && PALETTER.includes(p) ? p : "brun";
  } catch {
    return "brun";
  }
}

function applyPalett(p: Palett): void {
  const el = document.documentElement;
  if (p === "blaa") delete el.dataset.palett;
  else el.dataset.palett = p;
}

/** Kalles før React rendrer (i main.tsx), som initTheme. */
export function initPalett(): void {
  applyPalett(readPalett());
}

export function usePalett() {
  const [palett, setPalett] = useState<Palett>(readPalett);
  useEffect(() => {
    applyPalett(palett);
    try {
      localStorage.setItem(PALETT_KEY, palett);
    } catch {
      /* ignore */
    }
  }, [palett]);
  return [palett, setPalett] as const;
}
